import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BellIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  CalendarIcon,
  UserGroupIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector } from "hooks/hooks";
import { socket } from "utils/socket";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";

interface AdminNotification {
  id: string;
  type: "reservation_attempt" | "contact_message" | "new_user" | "location_pending" | "urgent" | "info";
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: "low" | "medium" | "high" | "urgent";
  actionUrl?: string;
  data?: any;
}

interface NotificationCounts {
  reservationAttempts: number;
  contactMessages: number;
  pendingLocations: number;
  newUsers: number;
  urgent: number;
  total: number;
}

const AdminNotificationSystem: React.FC = () => {
  const userState = useAppSelector((state) => state?.auth?.user);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [counts, setCounts] = useState<NotificationCounts>({
    reservationAttempts: 0,
    contactMessages: 0,
    pendingLocations: 0,
    newUsers: 0,
    urgent: 0,
    total: 0,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<string>("all");

  // Check if user is admin
  const isAdmin = userState?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;

    // Load initial admin notifications
    socket.emit("load-admin-notifications", { userId: userState.id });

    // Listen for new notifications
    socket.on("admin-notification", (notification: AdminNotification) => {
      handleNewNotification(notification);
    });

    // Listen for notification counts update
    socket.on("admin-notification-counts", (newCounts: NotificationCounts) => {
      setCounts(newCounts);
    });

    return () => {
      socket.off("admin-notification");
      socket.off("admin-notification-counts");
    };
  }, [isAdmin, userState?.id]);

  const handleNewNotification = (notification: AdminNotification) => {
    setNotifications((prev) => [notification, ...prev.slice(0, 49)]); // Keep max 50 notifications

    // Update counts
    setCounts((prev) => ({
      ...prev,
      [getCountKey(notification.type)]: prev[getCountKey(notification.type) as keyof NotificationCounts] + 1,
      total: prev.total + 1,
    }));

    // Show toast for high priority notifications
    if (notification.priority === "high" || notification.priority === "urgent") {
      showPriorityToast(notification);
    }
  };

  const getCountKey = (type: string): string => {
    switch (type) {
      case "reservation_attempt":
        return "reservationAttempts";
      case "contact_message":
        return "contactMessages";
      case "location_pending":
        return "pendingLocations";
      case "new_user":
        return "newUsers";
      case "urgent":
        return "urgent";
      default:
        return "total";
    }
  };

  const showPriorityToast = (notification: AdminNotification) => {
    const icon = getNotificationIcon(notification.type);
    toast.custom(
      (t) => (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className={`bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border-l-4 max-w-md ${
            notification.priority === "urgent" ? "border-red-500" : "border-orange-500"
          }`}
        >
          <div className="flex items-start">
            <div
              className={`flex-shrink-0 ${
                notification.priority === "urgent" ? "text-red-500" : "text-orange-500"
              }`}
            >
              {icon}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{notification.title}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{notification.message}</p>
              <div className="mt-3 flex space-x-2">
                {notification.actionUrl && (
                  <Link
                    to={notification.actionUrl}
                    onClick={() => {
                      toast.dismiss(t.id);
                      markAsRead(notification.id);
                    }}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-full transition-colors"
                  >
                    Voir
                  </Link>
                )}
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded-full transition-colors"
                >
                  Ignorer
                </button>
              </div>
            </div>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      ),
      {
        duration: notification.priority === "urgent" ? 8000 : 5000,
        position: "top-right",
      }
    );
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "reservation_attempt":
        return <CalendarIcon className="w-5 h-5" />;
      case "contact_message":
        return <EnvelopeIcon className="w-5 h-5" />;
      case "new_user":
        return <UserGroupIcon className="w-5 h-5" />;
      case "urgent":
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      default:
        return <BellIcon className="w-5 h-5" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "text-red-500 bg-red-50 border-red-200";
      case "high":
        return "text-orange-500 bg-orange-50 border-orange-200";
      case "medium":
        return "text-yellow-500 bg-yellow-50 border-yellow-200";
      default:
        return "text-blue-500 bg-blue-50 border-blue-200";
    }
  };

  const markAsRead = (notificationId: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)));
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setCounts((prev) => ({ ...prev, total: 0 }));
    if (userState?.id) {
      socket.emit("mark-all-admin-notifications-read", { userId: userState.id });
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "all") return true;
    if (filter === "unread") return !n.read;
    return n.type === filter;
  });

  if (!isAdmin) return null;

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        <BellIcon className="w-6 h-6" />
        {counts.total > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
          >
            {counts.total > 99 ? "99+" : counts.total}
          </motion.span>
        )}
      </motion.button>

      {/* Notification Dashboard */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -10 }}
            className="absolute top-12 right-0 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50"
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications Admin</h3>
                <div className="flex items-center space-x-2">
                  {counts.total > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Tout marquer comme lu
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className="text-center p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-red-600 dark:text-red-400 font-semibold">{counts.reservationAttempts}</div>
                  <div className="text-red-500 dark:text-red-400">Tentatives</div>
                </div>
                <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-blue-600 dark:text-blue-400 font-semibold">{counts.contactMessages}</div>
                  <div className="text-blue-500 dark:text-blue-400">Messages</div>
                </div>
                <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-green-600 dark:text-green-400 font-semibold">{counts.newUsers}</div>
                  <div className="text-green-500 dark:text-green-400">Nouveaux</div>
                </div>
              </div>

              {/* Filters */}
              <div className="flex space-x-1 mt-3">
                {["all", "unread", "reservation_attempt", "contact_message"].map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setFilter(filterType)}
                    className={`px-2 py-1 text-xs rounded-full transition-colors ${
                      filter === filterType
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    {filterType === "all" && "Tout"}
                    {filterType === "unread" && "Non lus"}
                    {filterType === "reservation_attempt" && "Réservations"}
                    {filterType === "contact_message" && "Messages"}
                  </button>
                ))}
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <BellIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune notification pour le moment</p>
                </div>
              ) : (
                filteredNotifications.map((notification) => (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      !notification.read ? "bg-blue-50/50 dark:bg-blue-900/10" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`flex-shrink-0 p-1.5 rounded-full ${getPriorityColor(notification.priority)}`}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {notification.title}
                          </p>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2"></div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {new Date(notification.timestamp).toLocaleString("fr-FR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              day: "2-digit",
                              month: "2-digit",
                            })}
                          </div>
                          <div className="flex items-center space-x-2">
                            {notification.actionUrl && (
                              <Link
                                to={notification.actionUrl}
                                onClick={() => {
                                  markAsRead(notification.id);
                                  setIsOpen(false);
                                }}
                                className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded transition-colors"
                              >
                                Voir
                              </Link>
                            )}
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                              >
                                <CheckIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            {filteredNotifications.length > 0 && (
              <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                <Link
                  to="/admin/dashboard/notifications"
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 py-1"
                >
                  Voir toutes les notifications →
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminNotificationSystem;
